import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useTrackEvent } from '@services/statsig/hooks/useTrackEvent';
import { enhancedApi as adminApi } from '@store/generated/admin';
import type { PayerSyncResponse, PayerSyncStatusResponse } from '@store/generated/admin';

// User-friendly hook names
const {
  usePostV2AdminPayersSyncMutation: useSyncPayersMutation,
  useGetV2AdminPayersSyncStatusQuery: useGetSyncStatusQuery,
} = adminApi;

// Async sync response type (new API structure)
interface AsyncPayerSyncResponse {
  task_id: string;
  message: string;
  status: string;
}

/**
 * Format timestamp to human-readable relative time
 */
function formatRelativeTime(timestamp: string | null | undefined): string {
  if (!timestamp) return 'Never synced';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Admin Payer Sync Page
 * Allows admins to sync payer data from Stedi API
 */
export default function PayerSyncAdminPage() {
  const { trackEvent, trackError } = useTrackEvent();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isSyncInProgress, setIsSyncInProgress] = useState(false);
  const [syncTaskId, setSyncTaskId] = useState<string | null>(null);
  const [syncStartTime, setSyncStartTime] = useState<Date | null>(null);
  const [previousSyncTime, setPreviousSyncTime] = useState<string | null | undefined>(null);
  const [previousPayerCount, setPreviousPayerCount] = useState<number | null>(null);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const [completionData, setCompletionData] = useState<{
    payerCount: number;
    payerCountChange: number;
  } | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch sync status
  const {
    data: syncStatus,
    isLoading: isLoadingStatus,
    isError: isStatusError,
    error: statusError,
    refetch: refetchStatus,
  } = useGetSyncStatusQuery(undefined, {
    // Poll every 8 seconds when sync is in progress
    pollingInterval: isSyncInProgress ? 8000 : 0,
  });

  // Sync mutation
  const [syncPayers, { isLoading: isSyncing, isError: isSyncError, error: syncError }] =
    useSyncPayersMutation();

  // Track page view
  useEffect(() => {
    trackEvent('admin_payer_sync_page_viewed', {
      metadata: {},
    });
  }, [trackEvent]);

  // Track errors
  useEffect(() => {
    if (isStatusError && statusError) {
      trackError(statusError, 'payer_sync_status');
    }
    if (isSyncError && syncError) {
      trackError(syncError, 'payer_sync');
    }
  }, [isStatusError, statusError, isSyncError, syncError, trackError]);

  // Handle sync button click
  const handleSyncClick = useCallback(() => {
    // Clear any previous completion message
    setShowCompletionMessage(false);
    setCompletionData(null);
    setConfirmDialogOpen(true);
    trackEvent('admin_payer_sync_button_clicked', {
      metadata: {},
    });
  }, [trackEvent]);

  // Handle sync confirmation
  const handleConfirmSync = useCallback(async () => {
    setConfirmDialogOpen(false);

    trackEvent('admin_payer_sync_confirmed', {
      metadata: {},
    });

    try {
      // Store previous state to detect completion
      if (syncStatus) {
        setPreviousSyncTime(syncStatus.last_synced_at);
        setPreviousPayerCount(syncStatus.payer_count);
      }

      // Start sync - returns immediately with task_id
      const result = await syncPayers().unwrap();

      // Handle both old sync response (for backward compatibility) and new async response
      if ('task_id' in result) {
        // New async response
        const asyncResult = result as unknown as AsyncPayerSyncResponse;
        setSyncTaskId(asyncResult.task_id);
        setIsSyncInProgress(true);
        setSyncStartTime(new Date());

        trackEvent('admin_payer_sync_started', {
          metadata: {
            task_id: asyncResult.task_id,
            status: asyncResult.status,
          },
        });
      } else {
        // Old sync response (if API hasn't been updated yet)
        const oldResult = result as PayerSyncResponse & { total_processed?: number };
        setIsSyncInProgress(false);
        trackEvent('admin_payer_sync_completed', {
          metadata: {
            success: String(oldResult.success),
            total_processed: String(oldResult.total_processed || 0),
          },
        });
      }
    } catch (error) {
      setIsSyncInProgress(false);
      trackError(error, 'payer_sync');
    }
  }, [syncPayers, syncStatus, trackEvent, trackError]);

  // Handle cancel
  const handleCancelSync = useCallback(() => {
    setConfirmDialogOpen(false);
    trackEvent('admin_payer_sync_cancelled', {
      metadata: {},
    });
  }, [trackEvent]);

  // Detect sync completion by polling status
  useEffect(() => {
    if (!isSyncInProgress || !syncStatus) return;

    const currentSyncTime = syncStatus.last_synced_at;
    const currentPayerCount = syncStatus.payer_count;

    // Check if sync completed by comparing timestamps
    if (
      previousSyncTime !== null &&
      currentSyncTime &&
      currentSyncTime !== previousSyncTime
    ) {
      // Sync completed! last_synced_at updated
      setIsSyncInProgress(false);
      setShowCompletionMessage(true);
      setCompletionData({
        payerCount: currentPayerCount,
        payerCountChange:
          previousPayerCount !== null ? currentPayerCount - previousPayerCount : 0,
      });

      const syncDuration = syncStartTime
        ? Math.round((new Date().getTime() - syncStartTime.getTime()) / 1000)
        : 0;

      trackEvent('admin_payer_sync_completed', {
        metadata: {
          task_id: syncTaskId || 'unknown',
          duration_seconds: String(syncDuration),
          payer_count: String(currentPayerCount),
          payer_count_changed: String(
            previousPayerCount !== null ? currentPayerCount - previousPayerCount : 0,
          ),
        },
      });

      // Clear sync state after a delay
      setTimeout(() => {
        setSyncTaskId(null);
        setSyncStartTime(null);
        setPreviousSyncTime(null);
        setPreviousPayerCount(null);
      }, 5000);
    } else if (
      syncStartTime &&
      new Date().getTime() - syncStartTime.getTime() > 180000
    ) {
      // Sync taking longer than 3 minutes - show warning
      // Don't stop polling, but user will see the warning
    }
  }, [
    isSyncInProgress,
    syncStatus,
    previousSyncTime,
    previousPayerCount,
    syncStartTime,
    syncTaskId,
    trackEvent,
  ]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Payer Data Sync
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Sync the latest payer information from Stedi&apos;s API
        </Typography>
      </Box>

      {/* Sync Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Sync Status</Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => refetchStatus()}
              disabled={isLoadingStatus}
            >
              Refresh
            </Button>
          </Box>

          {isLoadingStatus ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : isStatusError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load sync status. Please try again.
            </Alert>
          ) : syncStatus ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Last synced
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatRelativeTime(syncStatus.last_synced_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Total payers
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {syncStatus.payer_count.toLocaleString()} payers
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Typography variant="body1">{syncStatus.message}</Typography>
              </Grid>
            </Grid>
          ) : null}
        </CardContent>
      </Card>

      {/* Sync Button */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" gutterBottom>
                Sync Payers from Stedi
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This will fetch the latest payer data from Stedi and update the database.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={isSyncing || isSyncInProgress ? <CircularProgress size={20} /> : <SyncIcon />}
              onClick={handleSyncClick}
              disabled={isSyncing || isSyncInProgress}
              sx={{ ml: 2 }}
            >
              {isSyncing
                ? 'Starting...'
                : isSyncInProgress
                  ? 'Sync In Progress...'
                  : 'Sync Payers'}
            </Button>
          </Box>

          {/* Sync in progress indicator */}
          {isSyncInProgress && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Sync in progress...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The sync is running in the background. This page will automatically update when
                  the sync completes.
                  {syncTaskId && (
                    <>
                      <br />
                      Task ID: {syncTaskId}
                    </>
                  )}
                </Typography>
              </Alert>
              {syncStartTime && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Started {formatRelativeTime(syncStartTime.toISOString())}
                    {new Date().getTime() - syncStartTime.getTime() > 180000 && (
                      <> • Taking longer than expected. Check Celery logs if needed.</>
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Sync started successfully */}
          {isSyncing && !isSyncInProgress && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info">
                Starting sync task...
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Sync Completion Message */}
      {showCompletionMessage && syncStatus && completionData && (
        <Card sx={{ mb: 3, border: '2px solid', borderColor: 'success.main' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CheckCircleIcon color="success" />
              <Typography variant="h6" color="success.main">
                Sync Completed Successfully
              </Typography>
            </Box>

            <Alert severity="success" sx={{ mb: 2 }}>
              {syncStatus.message || 'Payer sync completed successfully.'}
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Total payers
                </Typography>
                <Typography variant="h6">
                  {completionData.payerCount.toLocaleString()} payers
                </Typography>
                {completionData.payerCountChange !== 0 && (
                  <Typography variant="caption" color="success.main">
                    {completionData.payerCountChange > 0 ? '+' : ''}
                    {completionData.payerCountChange.toLocaleString()} payers
                    {completionData.payerCountChange > 0 ? ' added' : ' removed'}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Last synced
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatRelativeTime(syncStatus.last_synced_at)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    For detailed sync statistics (created, updated, errors), check the Celery
                    worker logs.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Sync Error Display */}
      {isSyncError && syncError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Failed to Start Sync
          </Typography>
          <Typography variant="body2">
            The sync task could not be started. This may indicate a Celery worker issue. Please try
            again, or contact support if the problem persists.
          </Typography>
        </Alert>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelSync}>
        <DialogTitle>Confirm Payer Sync</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will sync the latest payer data from Stedi&apos;s API. The sync will:
            <ul>
              <li>Fetch the latest payer information from Stedi</li>
              <li>Add any new payers to the database</li>
              <li>Update existing payers with new information</li>
            </ul>
            This operation is safe and will not delete any existing payer data. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSync}>Cancel</Button>
          <Button onClick={handleConfirmSync} variant="contained" autoFocus>
            Sync Now
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

